import PropTypes from "prop-types";
import React, { useState, useRef, useMemo } from "react";
import ReactTooltip from "react-tooltip";
import BirthdayFields from "./BirthdayFields";
import {
  withValidation,
  FormField,
  Icon,
  NewCheckbox
} from "../../shared-components";
import { useTrans } from "../../hooks";
import PasswordFields from "../../shared-components/PasswordFields";
import { Button, ButtonVariant } from "@wattpad/web-ui-library";

const EmailSignup = ({ formType, nextUrl = "/home", toggleFormView }) => {
  const { trans } = useTrans();

  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isBirthdayValid, setIsBirthdayValid] = useState(false);
  const [isPolicyConsentValid, setIsPolicyConsentValid] = useState(false);
  const [usernameValue, setUsernameValue] = useState();

  const usernameInput = useRef();
  const emailInput = useRef();
  const passwordInput = useRef();
  const birthdayInput = useRef();

  const ValidatedFormField = useMemo(() => withValidation(FormField), []);

  // In order for the model to work properly in React needs to be hold as a reference
  const validationModel = useRef(
    window.app?.models && new window.app.models.Authsignup()
  );

  const validateSignupForm = (isValid, key) => {
    if (key.toLowerCase().includes("username")) {
      setIsUsernameValid(isValid);
    } else if (key.toLowerCase().includes("email")) {
      setIsEmailValid(isValid);
    } else if (key.toLowerCase().includes("password")) {
      setIsPasswordValid(isValid);
    } else if (key.toLowerCase().includes("birthday")) {
      setIsBirthdayValid(isValid);
    }
  };

  const actionURL = `/signup?nextUrl=${nextUrl}`;
  const signupFrom = `new_landing_signup`;
  const policy_agreement = trans("I have read and agree to Wattpad's <a href='/terms'>Terms of Service</a> and <a href='/privacy'>Privacy Policy</a>"); // prettier-ignore
  const isValidated =
    isPasswordValid &&
    isEmailValid &&
    isUsernameValid &&
    isBirthdayValid &&
    isPolicyConsentValid;

  const isFormValid = () => {
    if (!isPasswordValid) {
      passwordInput.current.focus();
      return false;
    } else if (!isEmailValid) {
      emailInput.current.focus();
      return false;
    } else if (!isUsernameValid) {
      usernameInput.current.focus();
      return false;
    } else if (!isBirthdayValid) {
      birthdayInput.current.focus();
      return false;
    } else if (!isPolicyConsentValid) {
      return false;
    }
    return true;
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    if (!isFormValid()) return;
    $.ajax({
      type: "post",
      url: actionURL,
      data: $("#signupForm").serialize()
    })
      .done(res => {
        window.location.replace(res.redirectUrl).reload();
      })
      .fail(err => {
        const message =
          err.responseJSON?.msg ||
          trans("Something went wrong. Please try again");
        wattpad.utils.showToast(message, { type: "dismissable" });
      });
  };

  const renderLabelWithHint = (label, ariaLabel, tooltip) => (
    <div className="field-label">
      <span className="field-label-text">{label}</span>
      <div aria-label={ariaLabel} data-tip={tooltip}>
        <Icon
          height="16"
          iconName="fa-info"
          color="wp-neutral-1"
          className="tooltip-icon"
        />
      </div>
    </div>
  );

  return (
    <>
      <form
        id="signupForm"
        action={actionURL}
        method="POST"
        onSubmit={handleFormSubmit}
      >
        <ReactTooltip className="tool-tip" effect="solid" />
        <input type="hidden" name="signup-from" value={signupFrom} />
        <input type="hidden" name="form-type" value={formType || ""} />
        <div className="input-group-vertical">
          {renderLabelWithHint(
            trans("Username"),
            "username hint",
            trans(
              "You do not have to use your real name. You can choose to use another name to protect your privacy."
            )
          )}
          <ValidatedFormField
            name="username"
            form="signup"
            inputType="text"
            title={trans("Enter username")}
            inputRef={usernameInput}
            autofocus
            validationModel={validationModel.current}
            showLabel={true}
            onValid={key => validateSignupForm(true, key)}
            onInvalid={key => validateSignupForm(false, key)}
            onBlur={() => setUsernameValue(usernameInput.current.value)}
          />
          <ValidatedFormField
            name="email"
            label={trans("E-mail")}
            form="signup"
            inputType="text"
            title={trans("Enter E-mail")}
            validationModel={validationModel.current}
            inputRef={emailInput}
            showLabel={true}
            onValid={key => validateSignupForm(true, key)}
            onInvalid={key => validateSignupForm(false, key)}
          />
          <PasswordFields
            username={usernameValue}
            validatePassword={setIsPasswordValid}
          />
          {renderLabelWithHint(
            trans("Birthday"),
            "why we ask for your birthday",
            trans(
              "You need to enter the date you were born. This information will only be visible to you and Wattpad’s Support teams."
            )
          )}
          <div className="birthday-fields">
            <BirthdayFields
              inputRef={birthdayInput}
              validationModel={validationModel.current}
              onValid={() => validateSignupForm(true, "birthday")}
              onInvalid={() => validateSignupForm(false, "birthday")}
            />
          </div>
          <div className="email-policy-agreement-container">
            <NewCheckbox
              contentId="policy-consent"
              required
              checked={isPolicyConsentValid}
              onChange={() => setIsPolicyConsentValid(!isPolicyConsentValid)}
              color="ds-base-2-60"
            />
            <span
              className="policy-agreement"
              dangerouslySetInnerHTML={{
                __html: policy_agreement
              }}
            />
          </div>
        </div>
        <Button
          fullWidth
          disabled={!isValidated}
          variant={ButtonVariant.PRIMARY}
          aria-label="Sign up with email"
        >
          {trans("Sign up with email")}
        </Button>
      </form>

      <div className="back-link-container">
        <button className="back-link" onClick={toggleFormView}>
          <span className="fa fa-left fa-wp-neutral-1 back-icon" />
          {trans("Back to all signup options")}
        </button>
      </div>
    </>
  );
};

EmailSignup.propTypes = {
  formType: PropTypes.string,
  nextUrl: PropTypes.string,
  toggleFormView: PropTypes.func.isRequired
};

export default EmailSignup;
